import type { ComponentType } from 'react';
import { useState } from 'react';
import ReactSelect, { type MultiValue } from 'react-select';

import { useIntl } from '../../hooks';
import { useTelemetry } from '../../hooks/useTelemetry.js';
import { ALL_METRICS, type MetricName } from '../../types/telemetry.js';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui';
import { TelemetryLineChart } from './TelemetryLineChart.js';

interface DeviceTelemetryDialogProps {
  deviceId: string;
  deviceSerialNumber: string;
  onOpenChange: (open: boolean) => void;
}

interface MetricOption {
  value: MetricName;
  label: string;
}

type SelectStyles = {
  control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => object;
  menu: (provided: Record<string, unknown>) => object;
  option: (provided: Record<string, unknown>, state: { isFocused: boolean }) => object;
  placeholder: (provided: Record<string, unknown>) => object;
  multiValueRemove: (provided: Record<string, unknown>) => object;
  multiValue: (provided: Record<string, unknown>) => object;
  multiValueLabel: (provided: Record<string, unknown>) => object;
};

const ReactSelectComponent = ReactSelect as unknown as ComponentType<{
  value: MetricOption[];
  onChange: (value: MultiValue<MetricOption>) => void;
  options: MetricOption[];
  isMulti: true;
  placeholder: string;
  className?: string;
  classNamePrefix?: string;
  isClearable?: boolean;
  styles?: SelectStyles;
}>;

const selectStyles: SelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 6,
    borderColor: 'var(--color-border)',
    color: 'var(--color-foreground)',
    backgroundColor: 'var(--color-input)',
    boxShadow: state.isFocused ? '0 0 0 2px #3b82f6' : 'none',
    ':hover': {
      borderColor: 'var(--color-border)',
      cursor: 'pointer',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
    backgroundColor: 'var(--color-background-light)',
    border: '1px solid var(--color-border)',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'var(--color-background-light)',
    color: 'var(--color-foreground)',
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--color-foreground)',
  }),
  multiValueRemove: (base) => ({
    ...base,
    ':hover': {
      backgroundColor: 'var(--color-destructive)',
      color: 'var(--color-white)',
    },
  }),
  option: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-background-light)',
    color: 'var(--color-foreground)',
    ':hover': {
      backgroundColor: 'var(--color-background-dark)',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--color-foreground)',
  }),
};

function get24HoursAgo(): string {
  const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const REFRESH_INTERVALS = [
  { value: 30_000, labelKey: '30000' },
  { value: 60_000, labelKey: '60000' },
  { value: 300_000, labelKey: '300000' },
  { value: 600_000, labelKey: '600000' },
  { value: 1_800_000, labelKey: '1800000' },
] as const;

export default function DeviceTelemetryDialog({
  deviceId,
  deviceSerialNumber,
  onOpenChange,
}: DeviceTelemetryDialogProps) {
  const intl = useIntl();

  const allMetricOptions: MetricOption[] = ALL_METRICS.map((m) => ({
    value: m,
    label: intl.formatMessage({ id: `deviceTelemetry.metrics.${m}` }),
  }));

  const DEFAULT_METRICS: MetricName[] = [
    'device_temperature',
    'device_humidity',
    'power_supply_voltage',
    'battery_voltage',
  ];
  const [selectedMetrics, setSelectedMetrics] = useState<MetricOption[]>(
    allMetricOptions.filter((o) => DEFAULT_METRICS.includes(o.value)),
  );
  const [from, setFrom] = useState(get24HoursAgo);
  const [to, setTo] = useState('');
  const [draftFrom, setDraftFrom] = useState(get24HoursAgo);
  const [draftTo, setDraftTo] = useState('');
  const [refreshInterval, setRefreshInterval] = useState<number>(300_000);

  const activeMetrics = selectedMetrics.map((o) => o.value);

  const results = useTelemetry({
    deviceId,
    metrics: activeMetrics,
    from: from || undefined,
    to: to || undefined,
    refetchInterval: refreshInterval,
  });

  const handleApply = () => {
    setFrom(draftFrom);
    setTo(draftTo);
  };

  const handleReset = () => {
    setDraftFrom('');
    setDraftTo('');
    setFrom('');
    setTo('');
  };

  const hasFilters = Boolean(from || to);

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl w-[95vw] h-[90vh] flex flex-col overflow-hidden">
        <DialogTitle className="text-lg font-semibold">
          {intl.formatMessage({ id: 'deviceTelemetry.title' }, { serial: deviceSerialNumber })}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {intl.formatMessage({ id: 'deviceTelemetry.title' }, { serial: deviceSerialNumber })}
        </DialogDescription>

        <div className="flex flex-col gap-4 flex-1 overflow-hidden">
          {/* Filter bar */}
          <div className="flex flex-wrap items-end gap-3 bg-gray-light rounded-lg p-4">
            <div className="flex flex-col gap-1 min-w-[280px] flex-1">
              <label className="text-sm font-medium text-foreground">
                {intl.formatMessage({ id: 'deviceTelemetry.filters.metrics' })}
              </label>
              <ReactSelectComponent
                value={selectedMetrics}
                onChange={(selected) => setSelectedMetrics([...selected])}
                options={allMetricOptions}
                isMulti={true}
                placeholder={intl.formatMessage({ id: 'deviceTelemetry.filters.selectMetrics' })}
                classNamePrefix="telemetry-metric-select"
                isClearable={true}
                styles={selectStyles}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">
                {intl.formatMessage({ id: 'deviceTelemetry.filters.fromDate' })}
              </label>
              <input
                type="datetime-local"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="px-3 py-2 border border-border bg-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">
                {intl.formatMessage({ id: 'deviceTelemetry.filters.toDate' })}
              </label>
              <input
                type="datetime-local"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
                className="px-3 py-2 border border-border bg-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">
                {intl.formatMessage({ id: 'deviceTelemetry.filters.refreshInterval' })}
              </label>
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                className="px-3 py-2 border border-border bg-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {REFRESH_INTERVALS.map(({ value, labelKey }) => (
                  <option key={value} value={value}>
                    {intl.formatMessage({ id: `deviceTelemetry.filters.intervals.${labelKey}` })}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 ml-auto">
              <Button variant="default" size="sm" onClick={handleApply}>
                {intl.formatMessage({ id: 'common.apply' })}
              </Button>
              {hasFilters && (
                <Button variant="outline" size="sm" onClick={handleReset}>
                  {intl.formatMessage({ id: 'common.reset' })}
                </Button>
              )}
            </div>
          </div>

          {/* Chart grid */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {activeMetrics.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-foreground text-sm">
                {intl.formatMessage({ id: 'deviceTelemetry.filters.selectMetrics' })}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pb-2">
                {activeMetrics.map((metricName, idx) => {
                  const result = results[idx];
                  return (
                    <TelemetryLineChart
                      key={metricName}
                      title={intl.formatMessage({ id: `deviceTelemetry.metrics.${metricName}` })}
                      data={result?.data?.data ?? []}
                      unit={
                        result?.data?.unit
                          ? intl.formatMessage({
                              id: `deviceTelemetry.units.${result.data.unit}`,
                            })
                          : null
                      }
                      isLoading={result?.isLoading ?? true}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
